<script setup lang="ts">
import { computed, watch, onMounted } from 'vue';
import { useCompanyStore } from '@/stores/company';
import { getInitials, formatTaxId, normalizeCnpj } from '@/utils/formatters';
import TruncatedText from '@/components/common/TruncatedText.vue';
import EntityMonitoringBadge from '@/components/entity-pulse/EntityMonitoringBadge.vue';
import type { EntityMonitoringView } from '@/composables/useEntityMonitoring';

const props = defineProps<{
  monitoring?: EntityMonitoringView | null;
}>();

const companyStore = useCompanyStore();

const companyData = computed(() => companyStore.generalData);
const dunsNumber = computed(() => companyStore.dunsNumber);

const initials = computed(() => {
    return getInitials(companyData.value?.razao_social)
});

const isActive = computed(() => {
    if (!companyData.value) return false;
    const status = companyData.value.situacao_text;
    return status === 'ATIVA';
});

// Extract CNPJ 14 from companyData.cnpj and fetch DUNS number
const extractCnpj14 = (cnpj: string | undefined): string | null => {
    if (!cnpj) return null;
    const cleanCnpj = normalizeCnpj(cnpj);
    // If already 14 characters, return as is
    if (cleanCnpj.length === 14) {
        return cleanCnpj;
    }
    return null;
};

// Fetch DUNS number when company data is available
const fetchDunsIfNeeded = async () => {
    if (!companyData.value?.cnpj) return;

    const cnpj14 = extractCnpj14(companyData.value.cnpj);
    if (cnpj14 && !dunsNumber.value) {
        try {
            await companyStore.fetchDunsNumber(cnpj14);
        } catch (error) {
            console.error('Error fetching DUNS number:', error);
        }
    }
};

// Watch for company data changes
watch(() => companyData.value?.cnpj, () => {
    fetchDunsIfNeeded();
}, { immediate: true });

// Also try on mount
onMounted(() => {
    fetchDunsIfNeeded();
});

</script>

<template>
    <div class="company-banner">
        <div class="banner-content">
            <div class="company-details-left">
                <div class="company-avatar" :class="{ 'company-avatar--loading': !companyData }">
                    <span v-if="companyData">{{ initials }}</span>
                    <span v-else class="avatar-spinner" aria-hidden="true"></span>
                </div>
                <div class="company-info">
                    <template v-if="companyData">
                        <h1 class="company-name">
                            <TruncatedText :text="companyData.razao_social" fallback="Razão Social não informada" />
                        </h1>
                        <p class="company-details">
                            <span>{{ formatTaxId(companyData.cnpj) }}</span>
                            <template v-if="dunsNumber">
                                <span class="separator">|</span>
                                <span>DUNS: {{ dunsNumber }}</span>
                            </template>
                            <span class="separator">|</span>
                            <span>Situação da Receita Federal:
                                <span class="status-text" :class="{ active: isActive }">
                                    {{ companyData.situacao_text }}
                                    <span v-if="isActive" class="material-icons check-icon">check_circle</span>
                                </span>
                            </span>
                        </p>
                    </template>
                    <template v-else>
                        <div class="skeleton-line skeleton-line--title" role="status" aria-label="Carregando dados da empresa"></div>
                        <div class="skeleton-line skeleton-line--subtitle"></div>
                    </template>
                </div>
            </div>
            <div v-if="props.monitoring?.isMonitored" class="relative z-10 flex-shrink-0">
                <EntityMonitoringBadge :monitoring="props.monitoring" />
            </div>
            <div class="banner-decoration">
                 <svg width="120" height="90" viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                    <g fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M40 80V20h50v60H40z M50 35h30 M50 50h30 M50 65h30" stroke-opacity="0.3"/>
                        <g transform="translate(10, 5)">
                        <circle cx="45" cy="35" r="20" stroke-opacity="0.7"/>
                        <line x1="60" y1="50" x2="75" y2="65" stroke-opacity="0.7" stroke-width="3"/>
                        </g>
                    </g>
                </svg>
            </div>
        </div>
    </div>
</template>

<style scoped>
.company-banner {
    background-color: var(--p-primary-500);
    color: var(--p-primary-50);
    padding: 1.5rem 2rem;
    border-radius: 16px;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    width: 100%;
    box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.2);
    position: relative;
    overflow: hidden;
}

.banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    width: 100%;
    /* Keep content (name, monitoring badge) clear of the absolute-positioned decoration */
    padding-right: 8rem;
}

.company-details-left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    z-index: 1;
    flex: 1;
    min-width: 0;
}

.company-avatar {
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 600;
    flex-shrink: 0;
}

.company-avatar--loading {
    border-color: rgba(255, 255, 255, 0.35);
}

.avatar-spinner {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    border: 2.5px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    animation: company-banner-spin 0.8s linear infinite;
}

@keyframes company-banner-spin {
    to { transform: rotate(360deg); }
}

.company-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    min-width: 0;
}

.skeleton-line {
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.25);
    animation: company-banner-pulse 1.4s ease-in-out infinite;
}

.skeleton-line--title {
    width: 14rem;
    max-width: 40vw;
    height: 1.35rem;
}

.skeleton-line--subtitle {
    width: 18rem;
    max-width: 55vw;
    height: 0.85rem;
}

@keyframes company-banner-pulse {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
    .avatar-spinner,
    .skeleton-line {
        animation: none;
    }
}

.company-name {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 0.25rem 0;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.company-details {
    font-size: 0.9rem;
    color: var(--p-primary-100);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    opacity: 0.9;
}

.separator {
    color: rgba(255, 255, 255, 0.3);
}

.status-text {
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
}

.status-text.active {
    color: #ffffff;
    font-weight: 600;
}

.check-icon {
    font-size: 1rem;
}

.banner-decoration {
    position: absolute;
    right: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 0;
    color: var(--p-primary-200);
    opacity: 0.5;
}

.banner-decoration svg {
    display: block;
}
</style>
